import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import {
    AutocompleteField,
    Button,
    Chip,
    DateField,
    Divider,
    IconButton,
    MenuItem,
    SelectField,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@curves/ui-base';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { GradientColorHeader } from '@Local/Components/Dialog/';
import { ReadTextField } from '@Local/Components';

import useClubApi from '@Apis/useClubApi';
import useCompanyApi from '@Apis/useCompanyApi';
import useMessageDialog from '@Util/hook/useMessageDialog';
import { refIsRequiredError, getErrorModuleStatus } from '@Util/utils';
import useValidationErrors from '@Util/hook/useValidationErrors';
import { initialDate } from '@Util/moment.util';
import { ACCOUNT_ENSURE_STATUS_TEXT_MAP } from '@Local/ClubCompany/staticData';

/**
 * 店舖總覽 新增／編輯 Dialog
 * 19 個店舖欄位，分 7 區呈現(基本／營運／聯絡／地址／循環訂單地址／加盟主／其他)
 * 公司欄：新增時可用 Autocomplete 選擇，編輯時唯讀；ClubCode 新增可填、編輯唯讀
 * 編輯模式下若循環訂單地址(郵遞區號／地址)有變更，送出前需二次確認，因會連帶更新既有循環訂單的收件地址
 * 新增送出成功後自動接著呼叫 ensure-accounts，並在本 Dialog 內顯示三個帳號的結果供複製(編輯模式不觸發)
 */
const ClubDialog = React.forwardRef((props, ref) => {
    const { open = false, onClose, sourceData: clubData, refresh } = props;
    const { postClubApi, putClubApi, getClubsOptionsApi, postEnsureAccountsApi } = useClubApi();
    const { getCompanyOptionsApi } = useCompanyApi();
    const setMessageDialog = useMessageDialog();
    const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();

    const [companyOptions, setCompanyOptions] = useState([]);
    const [clubStatusOptions, setClubStatusOptions] = useState([]);
    const [accountResults, setAccountResults] = useState(null);
    const [copiedKey, setCopiedKey] = useState('');

    const clubCodeRef = useRef(null);
    const clubNoRef = useRef(null);
    const clubNameRef = useRef(null);
    const companyRef = useRef(null);
    const clubStatusRef = useRef(null);
    const openingDateRef = useRef(null);
    const renewalDateRef = useRef(null);
    const openStartTimeRef = useRef(null);
    const openEndTimeRef = useRef(null);
    const clubTelRef = useRef(null);
    const clubFaxRef = useRef(null);
    const emailRef = useRef(null);
    const financialMailRef = useRef(null);
    const postCodeRef = useRef(null);
    const addressRef = useRef(null);
    const cyclePurchasePostCodeRef = useRef(null);
    const cyclePurchaseAddressRef = useRef(null);
    const ownerNameRef = useRef(null);
    const notesRef = useRef(null);

    const {
        clubID = 0,
        companyID = 0,
        companyName = '',
        clubCode = '',
        clubNo = '',
        clubName = '',
        clubStatus = 2,
        openingDate = '',
        renewalDate = '',
        openStartTime = '',
        openEndTime = '',
        clubTel = '',
        clubFax = '',
        email = '',
        financialMail = '',
        postCode = '',
        address = '',
        cyclePurchasePostCode = '',
        cyclePurchaseAddress = '',
        ownerName = '',
        notes = '',
    } = clubData || {};

    const isEdit = !!clubID;

    const fetchCompanyOptions = async () => {
        const resp = await getCompanyOptionsApi();
        setCompanyOptions(resp?.companyOptions ?? []);
    };

    const fetchClubStatusOptions = async () => {
        const resp = await getClubsOptionsApi(true);
        setClubStatusOptions(resp?.clubStatusOptions ?? []);
    };

    useEffect(() => {
        if (open) {
            setAccountResults(null);
            setCopiedKey('');
            clearValidationErrors();
            fetchClubStatusOptions();
            if (!isEdit) fetchCompanyOptions();
        }
        // eslint-disable-next-line
    }, [open]);

    const handleSubmit = async () => {
        const requiredRefs = [clubNoRef, clubNameRef, openStartTimeRef, openEndTimeRef, emailRef];
        if (!isEdit) requiredRefs.push(clubCodeRef, companyRef);

        const isError = refIsRequiredError(...requiredRefs);
        if (isError) return;

        const params = {
            clubID,
            companyID: isEdit ? companyID : companyRef.current.getResult('companyID'),
            clubCode: isEdit ? clubCode : clubCodeRef.current.getResult(),
            clubNo: clubNoRef.current.getResult(),
            clubName: clubNameRef.current.getResult(),
            clubStatus: Number(clubStatusRef.current.getResult()),
            openingDate: openingDateRef.current.getResult(),
            renewalDate: renewalDateRef.current.getResult(),
            openStartTime: openStartTimeRef.current.getResult(),
            openEndTime: openEndTimeRef.current.getResult(),
            clubTel: clubTelRef.current.getResult(),
            clubFax: clubFaxRef.current.getResult(),
            email: emailRef.current.getResult(),
            financialMail: financialMailRef.current.getResult(),
            postCode: postCodeRef.current.getResult(),
            address: addressRef.current.getResult(),
            cyclePurchasePostCode: cyclePurchasePostCodeRef.current.getResult(),
            cyclePurchaseAddress: cyclePurchaseAddressRef.current.getResult(),
            ownerName: ownerNameRef.current.getResult(),
            notes: notesRef.current.getResult(),
        };

        const isCyclePurchaseAddressChanged =
            isEdit && (params.cyclePurchasePostCode !== cyclePurchasePostCode || params.cyclePurchaseAddress !== cyclePurchaseAddress);

        if (isCyclePurchaseAddressChanged) {
            setMessageDialog({
                open: true,
                title: '確認變更循環訂單地址',
                msg: '此變更將連帶更新既有循環訂單的收件地址，確定要儲存嗎？',
                buttonText: '確認送出',
                onConfirm: () => submitClub(params),
            });
            return;
        }

        await submitClub(params);
    };

    const submitClub = async (params) => {
        const resp = isEdit ? await putClubApi(clubID, params) : await postClubApi(params);

        if (resp?.isError) {
            updateValidationErrors(resp.result);
            return;
        }

        if (resp === undefined || resp === null || resp === false) return;

        clearValidationErrors();

        if (!isEdit) {
            const ensureResp = await postEnsureAccountsApi(resp, { common: true, owner: true, admin: true });
            if (ensureResp?.common || ensureResp?.owner || ensureResp?.admin) {
                // 須等帳號建立完才重取，否則帳號欄是建立前的狀態
                if (typeof refresh === 'function') refresh();
                setAccountResults(ensureResp);
                return;
            }
        }

        if (typeof refresh === 'function') refresh();
        if (typeof onClose === 'function') onClose();
    };

    const handleAccountResultClose = () => {
        if (typeof refresh === 'function') refresh();
        if (typeof onClose === 'function') onClose();
    };

    const handleCopyAccount = (key, userName) => {
        if (!userName) return;
        navigator.clipboard.writeText(userName);
        setCopiedKey(key);
    };

    if (accountResults) {
        const accountRows = [
            { key: 'common', label: '通用', ...accountResults.common },
            { key: 'owner', label: '加盟主', ...accountResults.owner },
            { key: 'admin', label: '管理者', ...accountResults.admin },
        ];

        return (
            <Dialog open={open} fullWidth maxWidth={'md'}>
                <GradientColorHeader onClose={handleAccountResultClose}>新增店舖</GradientColorHeader>
                <DialogContent>
                    <Typography variant="body2" className={'mb-2'}>
                        店舖已建立，以下為系統自動檢查／建立的三個固定帳號，請複製提供給對應人員：
                    </Typography>
                    <Grid container spacing={2}>
                        {accountRows.map(({ key, label, userName, status }) => (
                            <Grid xs={12} item key={key}>
                                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                                    <Typography sx={{ width: '5.6rem' }}>{label}</Typography>
                                    <Typography sx={{ flex: 1 }}>{userName}</Typography>
                                    <Chip
                                        size={'small'}
                                        label={ACCOUNT_ENSURE_STATUS_TEXT_MAP[status] ?? status}
                                        color={status === 'failed' ? 'error' : status === 'created' ? 'success' : 'default'}
                                    />
                                    <Tooltip title={copiedKey === key ? '已複製' : '複製帳號'}>
                                        <span>
                                            <IconButton size={'small'} onClick={() => handleCopyAccount(key, userName)} disabled={!userName}>
                                                <ContentCopyIcon fontSize={'small'} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={handleAccountResultClose}>
                        完成
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} fullWidth maxWidth={'md'}>
            <GradientColorHeader onClose={onClose}>{isEdit ? '編輯店舖' : '新增店舖'}</GradientColorHeader>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid xs={12} item>
                        <Typography variant="subtitle1">基本</Typography>
                    </Grid>
                    <Grid xs={6} item>
                        {isEdit ? (
                            <ReadTextField label={'店舖代碼'} children={clubCode} />
                        ) : (
                            <TextField
                                key={clubCode}
                                ref={clubCodeRef}
                                label={'店舖代碼'}
                                defaultValue={clubCode}
                                fullWidth
                                required
                                {...getErrorModuleStatus(validationErrors, 'clubCode')}
                            />
                        )}
                    </Grid>
                    <Grid xs={6} item>
                        <TextField
                            key={clubNo}
                            ref={clubNoRef}
                            label={'店舖代號/報表'}
                            defaultValue={clubNo}
                            fullWidth
                            required
                            {...getErrorModuleStatus(validationErrors, 'clubNo')}
                        />
                    </Grid>
                    <Grid xs={6} item>
                        <TextField
                            key={clubName}
                            ref={clubNameRef}
                            label={'店舖名稱'}
                            defaultValue={clubName}
                            fullWidth
                            required
                            {...getErrorModuleStatus(validationErrors, 'clubName')}
                        />
                    </Grid>
                    <Grid xs={6} item>
                        {isEdit ? (
                            <ReadTextField label={'所屬公司'} children={companyName} />
                        ) : (
                            <AutocompleteField
                                ref={companyRef}
                                key={companyOptions}
                                label={'所屬公司'}
                                defaultValue={companyID || undefined}
                                options={companyOptions}
                                optionKeys={['companyName', 'taxID']}
                                primaryKey={'companyID'}
                                inputProps={{ placeholder: '請輸入公司名稱或統一編號' }}
                                fullWidth
                                required
                                {...getErrorModuleStatus(validationErrors, 'companyID')}
                            />
                        )}
                    </Grid>
                    <Grid xs={6} item>
                        <SelectField
                            key={`${clubStatus}-${clubStatusOptions.length}`}
                            ref={clubStatusRef}
                            label={'店舖狀態'}
                            defaultValue={String(clubStatus || 2)}
                            fullWidth
                        >
                            {clubStatusOptions.map(({ text, value }) => (
                                <MenuItem key={value} value={value}>
                                    {text}
                                </MenuItem>
                            ))}
                        </SelectField>
                    </Grid>
					<Grid xs={6} item>
						<TextField key={ownerName} ref={ownerNameRef} label={'加盟主名稱'} defaultValue={ownerName} fullWidth />
					</Grid>
					<Grid xs={12} item>
						<TextField key={notes} ref={notesRef} label={'備註'} defaultValue={notes} fullWidth multiline rows={3} />
					</Grid>

                    <Grid xs={12} item>
                        <Divider className={'my-2'} />
                        <Typography variant="subtitle1">營運</Typography>
                    </Grid>
                    <Grid xs={6} item>
                        <DateField
                            key={initialDate(openingDate)}
                            ref={openingDateRef}
                            label={'開幕時間'}
                            defaultValue={initialDate(openingDate)}
                            fullWidth
                        />
                    </Grid>
                    <Grid xs={6} item>
                        <DateField
                            key={initialDate(renewalDate)}
                            ref={renewalDateRef}
                            label={'續約起始日'}
                            defaultValue={initialDate(renewalDate)}
                            fullWidth
                        />
                    </Grid>
                    <Grid xs={6} item>
                        <TextField
                            key={openStartTime}
                            ref={openStartTimeRef}
                            type={'time'}
                            label={'營業起始時間'}
                            defaultValue={openStartTime}
                            fullWidth
                            required
                            {...getErrorModuleStatus(validationErrors, 'openStartTime')}
                        />
                    </Grid>
                    <Grid xs={6} item>
                        <TextField
                            key={openEndTime}
                            ref={openEndTimeRef}
                            type={'time'}
                            label={'營業結束時間'}
                            defaultValue={openEndTime}
                            fullWidth
                            required
                            {...getErrorModuleStatus(validationErrors, 'openEndTime')}
                        />
                    </Grid>

                    <Grid xs={12} item>
                        <Divider className={'my-2'} />
                        <Typography variant="subtitle1">聯絡</Typography>
                    </Grid>
                    <Grid xs={6} item>
                        <TextField key={clubTel} ref={clubTelRef} label={'店舖電話'} defaultValue={clubTel} fullWidth />
                    </Grid>
                    <Grid xs={6} item>
                        <TextField key={clubFax} ref={clubFaxRef} label={'店舖傳真'} defaultValue={clubFax} fullWidth />
                    </Grid>
                    <Grid xs={6} item>
                        <TextField
                            key={email}
                            ref={emailRef}
                            label={'店舖Email'}
                            defaultValue={email}
                            fullWidth
                            required
                            {...getErrorModuleStatus(validationErrors, 'email')}
                        />
                    </Grid>
                    <Grid xs={6} item>
                        <TextField key={financialMail} ref={financialMailRef} label={'財務Email'} defaultValue={financialMail} fullWidth />
                    </Grid>
                    <Grid xs={3} item>
                        <TextField key={postCode} ref={postCodeRef} label={'店舖郵遞區號'} defaultValue={postCode} fullWidth />
                    </Grid>
                    <Grid xs={9} item>
                        <TextField key={address} ref={addressRef} label={'店舖地址'} defaultValue={address} fullWidth />
                    </Grid>
                    <Grid xs={3} item>
                        <TextField
                            key={cyclePurchasePostCode}
                            ref={cyclePurchasePostCodeRef}
                            label={'循環訂單郵遞區號'}
                            defaultValue={cyclePurchasePostCode}
                            fullWidth
                        />
                    </Grid>
                    <Grid xs={9} item>
                        <TextField
                            key={cyclePurchaseAddress}
                            ref={cyclePurchaseAddressRef}
                            label={'循環訂單地址'}
                            defaultValue={cyclePurchaseAddress}
                            fullWidth
                        />
                    </Grid>

                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                    確認送出
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default ClubDialog;
